// React 
import { useEffect, useState } from 'react';

// Store
import useSiteStore from '../../store/siteStore.js';

// Components
import { FileDownloadOutlined, CloseOutlined, AccessTimeOutlined, HotelOutlined, FavoriteBorderOutlined, ScaleOutlined, AttachFileOutlined } from '@mui/icons-material';
import { Box, Chip, Divider, Grid, IconButton, Modal, Paper, Stack, Typography } from '@mui/material';
import { grey, blue, orange } from '@mui/material/colors/index.js';

// Hooks
import { useAppTheme } from '../../hooks/useAppTheme.js';

// Style
import { darkTheme } from '../../styles/colorSchemes/index.js';

// Other 
import { deleteFeedbackFile, getFeedbackFiles, getAllFeedbacksOfWeekOfAthlete } from '../../service/httpService/feedback/index.js';
import { formatDate, formatTime } from '../../utils/index.js';
import GiveFeedback from './GiveFeedback.js';

const Feedback = (props) => {
    const { athlete, deleteFilesAfterDownload, isFeedbackEditable, onClose, open, selectedFeedback, setSelectedFeedback, showFeedbackFiles, trainingSession } = props;
    const { weeklySchedule, setFeedbacks, year } = useSiteStore();
    const { isDarkMode } = useAppTheme();

    const [loaded, setLoaded] = useState(0);
    const [isAllDeleted, setIsAllDeleted] = useState(false);

    const primaryColor = isDarkMode ? orange[500] : '#1976d2';

    const handleFeedbackSuccess = (filesDeleted = false) => {
        // Refetch feedbacks to update the UI immediately
        getAllFeedbacksOfWeekOfAthlete(athlete.profile.email, year, weeklySchedule.calendarWeek)
            .then(response => {
                let currentFeedbacks = response.data;

                // Wenn aktueller User alle Dateien heruntergeladen hat, setzen wir hasNewFiles auf false
                if (filesDeleted) {
                    currentFeedbacks = currentFeedbacks.map(fb =>
                        fb.trainingsessionid === trainingSession.trainingsessionid
                            ? { ...fb, hasNewFiles: false }
                            : fb
                    );
                }

                setFeedbacks(currentFeedbacks);
            })
            .catch(() => {
                console.error('Failed to refresh feedbacks');
            });
    };

    useEffect(() => {
        if (open && weeklySchedule) {
            setLoaded(0);
            getFeedbackFiles(trainingSession.trainingsessionid, setLoaded)
                .then(response => {
                    let files = response.data;
                    setSelectedFeedback(prev => (
                        {
                            ...prev,
                            files: files,
                        }
                    ));
                    setLoaded(0);
                })
                .catch((error) => {
                    console.log("Info: Keine Dateien gefunden oder Feedback gelöscht.", error);
                    setSelectedFeedback(prev => (
                        {
                            ...prev,
                            files: [],
                        }
                    ));
                    setLoaded(0);
                });
        }
    }, [open, setSelectedFeedback, trainingSession.trainingsessionid, weeklySchedule]);

    // Überwacht Änderungen an der Dateiliste
    useEffect(() => {
        if (selectedFeedback && selectedFeedback.files) {
            setIsAllDeleted(selectedFeedback.files.length === 0);
        }
    }, [selectedFeedback]);

    const downloadFile = (file) => {
        const fileURL = window.URL.createObjectURL(new Blob([new Uint8Array(file.data.data).buffer]))
        let alink = document.createElement('a');
        alink.href = fileURL;
        alink.download = file.name;
        alink.click();

        if (deleteFilesAfterDownload) {
            deleteFeedbackFile(trainingSession.trainingsessionid, file.name)
                .then(() => {
                    // Lokal die Liste aktualisieren
                    setSelectedFeedback(prev => {
                        const currentFiles = prev.files || [];
                        const remainingFiles = currentFiles.filter(f => f.name !== file.name);
                        return {
                            ...prev,
                            files: remainingFiles,
                        };
                    });

                    setLoaded(0);
                })
                .catch((error) => {
                    console.error("Fehler beim Löschen der Datei:", error);
                    setLoaded(0);
                });
        }
    }

    if (isFeedbackEditable && !showFeedbackFiles) {
        return (
            <GiveFeedback
                loaded={loaded}
                setLoaded={setLoaded}
                trainingSession={trainingSession}
                feedback={selectedFeedback}
                open={open}
                onClose={onClose}
                athlete={athlete}
                dayOfWeek={trainingSession.dayOfWeek}
                onSuccess={handleFeedbackSuccess} />);
    }

    else {
        if (!selectedFeedback) {
            return (
                <Modal
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'auto',
                        p: 2
                    }}
                    open={open}
                    onClose={onClose}
                >
                    <Paper
                        elevation={8}
                        sx={{
                            width: { xs: '95%', sm: '80%', md: '60%' },
                            maxWidth: '500px',
                            p: { xs: 3, sm: 4 },
                            borderRadius: 3,
                            border: isDarkMode ? `1px solid ${darkTheme.cardBorder}` : 'none',
                            backgroundColor: isDarkMode ? darkTheme.bg : '#fafafa',
                            position: 'relative'
                        }}
                    >
                        {/* Close Button */}
                        <IconButton
                            onClick={onClose}
                            sx={{
                                position: 'absolute',
                                top: 12,
                                right: 12,
                                bgcolor: isDarkMode ? darkTheme.cardBg : 'rgba(255, 255, 255, 0.9)',
                                color: isDarkMode ? darkTheme.textSecondary : undefined,
                                '&:hover': {
                                    bgcolor: isDarkMode ? darkTheme.cardHover : 'rgba(0, 0, 0, 0.05)',
                                    transform: 'rotate(90deg)',
                                    transition: 'transform 0.3s ease'
                                }
                            }}
                        >
                            <CloseOutlined />
                        </IconButton>

                        <Box sx={{ textAlign: 'center', py: 2 }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 600,
                                    color: isDarkMode ? darkTheme.textPrimary : grey[700],
                                    mb: 1
                                }}
                            >
                                {athlete.hasExtendedFeedback === true
                                    ? 'Keine erweiterte Rückmeldung vorhanden'
                                    : 'Keine Rückmeldung vorhanden'
                                }
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ color: isDarkMode ? darkTheme.textSecondary : grey[500] }}
                            >
                                {formatDate(trainingSession.date)}
                            </Typography>
                        </Box>

                    </Paper>
                </Modal>
            );
        }

        else {
            return (
                <Modal
                    sx={{
                        display: 'flex',
                        p: 2,
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'auto'
                    }}
                    open={open}
                    onClose={onClose}
                >
                    <Paper
                        elevation={8}
                        sx={{
                            width: { xs: '90%', sm: '85%', md: '70%' },
                            maxWidth: '550px',
                            p: { xs: 2.5, sm: 3, md: 4 },
                            borderRadius: 3,
                            border: isDarkMode ? `1px solid ${darkTheme.cardBorder}` : 'none',
                            maxHeight: '85vh',
                            overflow: 'auto',
                            backgroundColor: isDarkMode ? darkTheme.bg : '#fafafa',
                            position: 'relative'
                        }}
                    >
                        {/* Close Button */}
                        <IconButton
                            onClick={e => {
                                onClose();
                                if (isAllDeleted) {
                                    handleFeedbackSuccess(true);
                                    if (props.onSuccess) props.onSuccess();
                                }
                            }}
                            sx={{
                                position: 'absolute',
                                top: 16,
                                right: 16,
                                zIndex: 10,
                                bgcolor: isDarkMode ? darkTheme.cardBg : 'rgba(255, 255, 255, 0.9)',
                                color: isDarkMode ? darkTheme.textSecondary : undefined,
                                '&:hover': {
                                    bgcolor: isDarkMode ? darkTheme.cardHover : 'rgba(0, 0, 0, 0.05)',
                                    transform: 'rotate(90deg)',
                                    transition: 'transform 0.3s ease'
                                }
                            }}
                        >
                            <CloseOutlined />
                        </IconButton>

                        {/* Header */}
                        <Box sx={{ mb: 2 }}>
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: 600,
                                    color: isDarkMode ? darkTheme.textPrimary : grey[800],
                                }}
                            >
                                {(!athlete.hasExtendedFeedback || showFeedbackFiles)
                                    ? 'Rückmeldung'
                                    : 'Erweiterte Rückmeldung'
                                }
                            </Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                <Typography
                                    variant="body2"
                                    sx={{ color: isDarkMode ? darkTheme.textSecondary : grey[500] }}
                                >
                                    {formatDate(trainingSession.date)}
                                </Typography>

                                {(selectedFeedback.lastEdited !== null && !isFeedbackEditable) && (
                                    <>
                                        <Typography variant="body2" sx={{ color: isDarkMode ? darkTheme.textSecondary : grey[400] }}>•</Typography>
                                        <Typography
                                            sx={{
                                                fontStyle: 'italic',
                                                color: isDarkMode ? darkTheme.textSecondary : grey[500],
                                                fontSize: '0.875rem'
                                            }}
                                            variant="body2"
                                        >
                                            Zuletzt geändert: {formatDate(selectedFeedback.lastEdited)}
                                        </Typography>
                                    </>
                                )}
                            </Box>
                        </Box>

                        <Divider sx={{ mb: 3, borderColor: isDarkMode ? darkTheme.cardBorder : undefined }} />

                        <Grid container spacing={2}>
                            {(!showFeedbackFiles && athlete.hasExtendedFeedback) && (
                                <>
                                    {/* Health Data Section Title */}
                                    <Grid item xs={12}>
                                        <Typography
                                            variant="subtitle1"
                                            sx={{
                                                fontWeight: 600,
                                                color: isDarkMode ? darkTheme.textPrimary : grey[700],
                                                mb: 0.5,
                                                fontSize: '1rem'
                                            }}
                                        >
                                            Gesundheitsdaten
                                        </Typography>
                                    </Grid>

                                    {/* Wake Up Time */}
                                    <Grid item xs={6}>
                                        <Box
                                            sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
                                                boxShadow: isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)',
                                                height: '100%'
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                <AccessTimeOutlined sx={{ fontSize: '1.1rem', color: primaryColor, mr: 1 }} />
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: isDarkMode ? darkTheme.textSecondary : grey[500], fontSize: '0.8rem' }}
                                                >
                                                    Weckzeit
                                                </Typography>
                                            </Box>
                                            <Typography
                                                sx={{
                                                    fontSize: '1.2rem',
                                                    fontWeight: 600,
                                                    color: isDarkMode ? darkTheme.textPrimary : grey[800]
                                                }}
                                            >
                                                {formatTime(selectedFeedback.extendedFeedback.wakeUpTime)} Uhr
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    {/* Sleep Duration */}
                                    <Grid item xs={6}>
                                        <Box
                                            sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
                                                boxShadow: isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)',
                                                height: '100%'
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                <HotelOutlined sx={{ fontSize: '1.1rem', color: primaryColor, mr: 1 }} />
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: isDarkMode ? darkTheme.textSecondary : grey[500], fontSize: '0.8rem' }}
                                                >
                                                    Schlafdauer
                                                </Typography>
                                            </Box>
                                            <Typography
                                                sx={{
                                                    fontSize: '1.2rem',
                                                    fontWeight: 600,
                                                    color: isDarkMode ? darkTheme.textPrimary : grey[800]
                                                }}
                                            >
                                                {selectedFeedback.extendedFeedback.sleepDuration} Stunden
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    {/* Resting Pulse */}
                                    <Grid item xs={6}>
                                        <Box
                                            sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
                                                boxShadow: isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)',
                                                height: '100%'
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                <FavoriteBorderOutlined sx={{ fontSize: '1.1rem', color: primaryColor, mr: 1 }} />
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: isDarkMode ? darkTheme.textSecondary : grey[500], fontSize: '0.8rem' }}
                                                >
                                                    Ruhepuls
                                                </Typography>
                                            </Box>
                                            <Typography
                                                sx={{
                                                    fontSize: '1.2rem',
                                                    fontWeight: 600,
                                                    color: isDarkMode ? darkTheme.textPrimary : grey[800]
                                                }}
                                            >
                                                {selectedFeedback.extendedFeedback.restingPulse} Bpm
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    {/* Weight */}
                                    <Grid item xs={6}>
                                        <Box
                                            sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
                                                boxShadow: isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)',
                                                height: '100%'
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                <ScaleOutlined sx={{ fontSize: '1.1rem', color: primaryColor, mr: 1 }} />
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: isDarkMode ? darkTheme.textSecondary : grey[500], fontSize: '0.8rem' }}
                                                >
                                                    Gewicht
                                                </Typography>
                                            </Box>
                                            <Typography
                                                sx={{
                                                    fontSize: '1.2rem',
                                                    fontWeight: 600,
                                                    color: isDarkMode ? darkTheme.textPrimary : grey[800]
                                                }}
                                            >
                                                {selectedFeedback.extendedFeedback.weight} Kg
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </>
                            )}

                            {showFeedbackFiles && (
                                <Grid item xs={12}>
                                    <Box
                                        sx={{
                                            p: 2.5,
                                            borderRadius: 2,
                                            backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
                                            boxShadow: isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <AttachFileOutlined sx={{ mr: 1, color: primaryColor }} />
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: isDarkMode ? darkTheme.textPrimary : grey[700]
                                                }}
                                            >
                                                Dateianhänge
                                            </Typography>
                                        </Box>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                flexWrap: 'wrap',
                                                alignContent: 'start',
                                                gap: 1.5
                                            }}
                                        >
                                            {selectedFeedback.files.length === 0 && (
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: isDarkMode ? darkTheme.textSecondary : grey[500],
                                                        fontStyle: 'italic',
                                                        py: 1
                                                    }}
                                                >
                                                    Keine Dateien vorhanden
                                                </Typography>
                                            )}
                                            {selectedFeedback.files.map((file, index) => {
                                                return (
                                                    <Stack key={index} direction="row">
                                                        <Chip
                                                            sx={{
                                                                height: 42,
                                                                borderTopRightRadius: 0,
                                                                borderBottomRightRadius: 0,
                                                                backgroundColor: isDarkMode ? darkTheme.cardHover : grey[100],
                                                                color: isDarkMode ? darkTheme.textPrimary : undefined,
                                                                fontWeight: 500,
                                                                maxWidth: '200px',
                                                                '& .MuiChip-label': {
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap'
                                                                }
                                                            }}
                                                            label={`${file.name}`}
                                                        />
                                                        <IconButton
                                                            disabled={file.isDeleted}
                                                            sx={{
                                                                '&.Mui-disabled': { bgcolor: isDarkMode ? darkTheme.cardBorder : grey[200] },
                                                                height: 42,
                                                                width: 42,
                                                                borderTopLeftRadius: 0,
                                                                borderBottomLeftRadius: 0,
                                                                borderTopRightRadius: 8,
                                                                borderBottomRightRadius: 8,
                                                                bgcolor: primaryColor,
                                                                color: 'white',
                                                                '&:hover': {
                                                                    bgcolor: isDarkMode ? orange[600] : blue[700]
                                                                }
                                                            }}
                                                            onClick={() => downloadFile(file)}
                                                        >
                                                            <FileDownloadOutlined />
                                                        </IconButton>
                                                    </Stack>
                                                )
                                            })
                                            }
                                        </Box>
                                    </Box>
                                </Grid>
                            )}
                        </Grid>

                    </Paper>
                </Modal>
            )
        }
    }
}

export default Feedback;